import { useCallback, useEffect, useRef, useState } from "react";

const DISMISS_KEY = "heldall_install_dismissed_at";
const DISMISS_WINDOW_MS = 14 * 24 * 60 * 60 * 1000; // hide for 14 days after "Not now"
const isDev = process.env.NODE_ENV !== "production";

function isStandalone() {
  try {
    return (
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true
    );
  } catch (_) {
    return false;
  }
}

function dismissedRecently() {
  try {
    const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return at > 0 && Date.now() - at < DISMISS_WINDOW_MS;
  } catch (_) {
    return false;
  }
}

// Wraps Chrome's beforeinstallprompt into a production-safe install affordance.
// Exposes `eligible` (show UI only when true), plus prompt/dismiss controls.
// No manifest / service-worker changes. Diagnostic logs are dev-only.
export function useInstallPrompt() {
  const deferred = useRef(null);
  const [canInstall, setCanInstall] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(dismissedRecently());
  const [standalone] = useState(isStandalone());

  useEffect(() => {
    const onBIP = (e) => {
      e.preventDefault();
      deferred.current = e;
      setCanInstall(true);
      if (isDev) console.info("[install] beforeinstallprompt fired", { platforms: e.platforms });
    };
    const onInstalled = () => {
      setInstalled(true);
      setCanInstall(false);
      deferred.current = null;
      try { localStorage.removeItem(DISMISS_KEY); } catch (_) {}
      if (isDev) console.info("[install] appinstalled fired");
    };
    window.addEventListener("beforeinstallprompt", onBIP);
    window.addEventListener("appinstalled", onInstalled);

    let t;
    if (isDev) {
      t = setTimeout(() => {
        if (!deferred.current) {
          const manifest = document.querySelector('link[rel="manifest"]')?.getAttribute("href") || null;
          console.warn("[install] beforeinstallprompt did NOT fire", {
            displayModeStandalone: isStandalone(),
            serviceWorkerController: !!(navigator.serviceWorker && navigator.serviceWorker.controller),
            manifestHref: manifest,
            userAgent: navigator.userAgent,
          });
        }
      }, 3500);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
      if (t) clearTimeout(t);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    const e = deferred.current;
    if (!e) return;
    e.prompt();
    try {
      const choice = await e.userChoice;
      if (isDev) {
        console.info("[install] userChoice", {
          outcome: choice && choice.outcome,
          standaloneNow: isStandalone(),
        });
      }
      if (choice && choice.outcome === "dismissed") {
        try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) {}
        setDismissed(true);
      }
    } catch (err) {
      if (isDev) console.warn("[install] prompt error", err);
    }
    deferred.current = null;
    setCanInstall(false);
  }, []);

  const dismiss = useCallback(() => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) {}
    setDismissed(true);
  }, []);

  const eligible = canInstall && !standalone && !installed && !dismissed;

  return { eligible, installed, promptInstall, dismiss };
}
